import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { ISidebarItem } from '../../interfaces/sidebar-item.interface';
import { IUser } from '../../model/user.model';
import { AuthService } from '../../services/auth.service';
import { SidebarState } from '../../store/sidebar/sidebar.state';
import { GetSelfStudiesAction } from '../../store/study/study.actions';
import { StudyState } from '../../store/study/study.state';
import { UserState } from '../../store/user/user.state';
import { SelfStudyDTO } from '../../dtos/study.dto';

// TODO: delete component/route, move logic to AuthState.authActionsLogin
@UntilDestroy()
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  imports: [TranslateModule],
})
export class HomeComponent implements OnInit {
  @Select(SidebarState.getSidebar) private readonly sidebar$: Observable<ISidebarItem[]>;
  @Select(UserState.getLoggedUser) private readonly loggedUser$: Observable<IUser>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public sidebarItems: any = [];
  public showLoginButton = false;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly store: Store,
  ) {}

  public ngOnInit(): void {
    // Self studies are not loaded by the UserResolver for superusers/client-admins, so they are requested here (cached for the other users).
    // If the request fails the user is treated as having no studies, falling back to the sidebar redirect
    const selfStudies$ = this.store.dispatch(new GetSelfStudiesAction()).pipe(
      catchError(() => of(null)),
      switchMap(() => this.store.select(StudyState.getSelfStudies)),
    );

    combineLatest([this.sidebar$, this.loggedUser$.pipe(filter((loggedUser) => !!loggedUser)), selfStudies$])
      .pipe(
        untilDestroyed(this),
        tap(([sidebarItems]) => {
          this.sidebarItems = sidebarItems;
        }),
        tap(([sidebarItems]) => {
          if (sidebarItems.length === 0) {
            this.showLoginButton = true;
          }
        }),
        filter(([sidebarItems]) => !!sidebarItems[0]),
        tap(
          ([sidebarItems, loggedUser, selfStudies]) => {
            let item = sidebarItems[0];

            const isSuperOrClientAdmin = loggedUser.isSuper() || loggedUser.isClientAdmin();
            const isCoordinatorOrSupervisor = selfStudies.some((study) => loggedUser.isCoordinatorOrSupervisor(study.organization, study.id));

            // TODO: Find solution to setTimeout - Remove setTimeout from component
            // The router.navigate call in this component were surrendered by a setTimeout call to avoit problem of users
            // staying stuck in the Home Component trying to get redirected to the correct landing page.
            // The reason why this happen was not found but having a timeout before the redirect solve the problem
            // A proper solution still need to be found
            if (selfStudies.length === 1 && !isSuperOrClientAdmin && !isCoordinatorOrSupervisor) {
              // Users with a single assigned study (e.g. board members) go directly to its dashboard
              const study = selfStudies[0];
              setTimeout(() => {
                void this.router.navigate(['/organizations', study.organization, 'study', study.id, 'dashboard']);
              }, 2);
            } else if (selfStudies.length > 0) {
              // Coordinators/supervisors, superusers/client-admins assigned to at least one study and
              // the other users with more than one study land on the studies overview page
              setTimeout(() => {
                void this.router.navigate(['/studies-overview']);
              }, 2);
            } else if (item.type === 'page') {
              // Users without assigned studies (e.g. superusers/client-admins) keep the sidebar first page redirect
              setTimeout(() => {
                void this.router.navigate([item.link]);
              }, 2);
            }
          },
          tap(() => {
            this.showLoginButton = true;
          }),
        ),
      )
      .subscribe();
  }

  public logout(): void {
    this.authService.logout();
  }
}
